import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";

const columns = [
  { key: "ingredient", label: "Insumo", width: "minmax(150px, 1.2fr)" },
  { key: "brand", label: "Marca", width: "minmax(140px, 1.15fr)" },
  {
    key: "presentation",
    label: "Presentación",
    width: "minmax(170px, 1.35fr)",
  },
  {
    key: "purchaseUnit",
    label: "Unidad de compra",
    width: "minmax(108px, .72fr)",
  },
  {
    key: "quantityPerUnit",
    label: "Cantidad por unidad",
    width: "minmax(112px, .76fr)",
  },
  {
    key: "consumptionUnit",
    label: "Unidad de consumo",
    width: "minmax(112px, .76fr)",
  },
  { key: "actions", label: "Acciones", width: "minmax(145px, .9fr)" },
];

const gridTemplate = columns.map((column) => column.width).join(" ");

const gridSx = {
  display: "grid",
  gridTemplateColumns: gridTemplate,
  columnGap: 1,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

const unitLabels = {
  g: "g",
  ml: "ml",
  unit: "unidad",
  package: "paquete",
};

function formatUnit(value) {
  return unitLabels[value] || value || "—";
}

function formatDecimal(value, maximumFractionDigits = 4) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(numeric);
}

function formatCost(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function TextCell({ children }) {
  return (
    <Typography
      noWrap
      title={typeof children === "string" ? children : undefined}
      sx={{
        color: "primary.900",
        fontSize: 13,
        fontWeight: 700,
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}

function DetailMetric({ label, value, emphasized = false }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        px: 1.6,
        py: 1.3,
        borderRadius: "14px",
        bgcolor: emphasized ? "secondary.100" : "neutral.50",
        boxShadow: "inset 0px 0px 2px rgba(96,96,113,.18)",
        textAlign: "center",
      }}
    >
      <Typography
        sx={{
          mb: 0.45,
          color: "neutral.700",
          fontSize: 10.5,
          fontWeight: 800,
          lineHeight: 1.2,
          letterSpacing: ".035em",
          textTransform: "uppercase",
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
      <Typography
        title={typeof value === "string" ? value : undefined}
        sx={{
          color: "primary.900",
          fontSize: emphasized ? 15 : 13,
          fontWeight: emphasized ? 800 : 700,
          lineHeight: 1.25,
          overflowWrap: "anywhere",
          textAlign: "center",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function PurchaseItemsTable({
  rows = [],
  loading = false,
  deletingId = null,
  expandedIds,
  onEdit,
  onDelete,
  onToggleExpanded,
  emptyMessage = "Todavía no hay ítems de compra cargados.",
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box
          sx={{
            minWidth: 1080,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              flexShrink: 0,
              px: 2,
              py: 1.45,
              bgcolor: "neutral.600",
              scrollbarGutter: "stable",
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  color: "logoblanco",
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              bgcolor: "neutral.50",
              scrollbarGutter: "stable",
            }}
          >
            {rows.map((item) => {
              const expanded = expandedIds?.has(item.id);
              const deleting = deletingId === item.id;

              return (
                <Box
                  key={item.id}
                  sx={{
                    borderBottom: "1px solid rgba(0,0,0,.07)",
                    bgcolor: expanded ? "secondary.50" : "transparent",
                    transition: "background-color 160ms ease",
                    "&:hover": {
                      bgcolor: expanded ? "secondary.50" : "rgba(0,0,0,.025)",
                    },
                  }}
                >
                  <Box sx={{ ...gridSx, minHeight: 58, px: 2, py: 1 }}>
                    <TextCell>{item.ingredient?.name || "—"}</TextCell>
                    <TextCell>{item.brand || "—"}</TextCell>
                    <TextCell>{item.presentation || "—"}</TextCell>
                    <TextCell>{item.purchaseUnit || "—"}</TextCell>
                    <TextCell>{formatDecimal(item.quantityPerUnit)}</TextCell>
                    <TextCell>{formatUnit(item.consumptionUnit)}</TextCell>

                    <Stack
                      direction="row"
                      spacing={0.65}
                      sx={{ justifyContent: "center", alignItems: "center" }}
                    >
                      <Tooltip title="Editar ítem" arrow>
                        <IconButton
                          size="small"
                          disabled={loading || deleting}
                          onClick={(event) => {
                            event.stopPropagation();
                            onEdit?.(item);
                          }}
                          aria-label={`Editar ${item.ingredient?.name || "ítem"} ${
                            item.brand || ""
                          }`.trim()}
                          sx={{
                            width: 31,
                            height: 31,
                            bgcolor: "neutral.50",
                            boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
                            "&:hover": { bgcolor: "secondary.100" },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 17, color: "primary.900" }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Eliminar ítem" arrow>
                        <IconButton
                          size="small"
                          disabled={loading || deleting}
                          onClick={(event) => {
                            event.stopPropagation();
                            onDelete?.(item);
                          }}
                          aria-label={`Eliminar ${item.ingredient?.name || "ítem"} ${
                            item.brand || ""
                          }`.trim()}
                          sx={{
                            width: 31,
                            height: 31,
                            bgcolor: "neutral.50",
                            boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
                            "&:hover": { bgcolor: "secondary.100" },
                          }}
                        >
                          <DeleteOutlineIcon
                            sx={{ fontSize: 18, color: "secondary.700" }}
                          />
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                        title={expanded ? "Ocultar detalle" : "Ver detalle"}
                        arrow
                      >
                        <IconButton
                          size="small"
                          disabled={loading || deleting}
                          onClick={() => onToggleExpanded?.(item)}
                          aria-label={`${expanded ? "Ocultar" : "Ver"} detalle de ${
                            item.ingredient?.name || "ítem"
                          } ${item.brand || ""}`.trim()}
                          aria-expanded={Boolean(expanded)}
                          sx={{
                            width: 31,
                            height: 31,
                            bgcolor: "secondary.500",
                            boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
                            "&:hover": { bgcolor: "secondary.600" },
                          }}
                        >
                          {expanded ? (
                            <ExpandLessIcon
                              sx={{ fontSize: 21, color: "logoblanco" }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              sx={{ fontSize: 21, color: "logoblanco" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {expanded && (
                    <Box
                      sx={{
                        px: { xs: 2, md: 2.5 },
                        pb: 1.8,
                        animation: "purchaseItemDetailIn 160ms ease-out",
                        "@keyframes purchaseItemDetailIn": {
                          from: { opacity: 0, transform: "translateY(-4px)" },
                          to: { opacity: 1, transform: "translateY(0)" },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "repeat(2, minmax(180px, 1fr))",
                            md: "minmax(150px, .8fr) minmax(220px, 1.35fr) minmax(170px, .9fr) minmax(240px, 1.4fr)",
                          },
                          gap: 1,
                          p: 1.1,
                          borderRadius: "18px",
                          bgcolor: "rgba(235, 235, 244, .72)",
                          boxShadow:
                            "inset 0px 0px 3px rgba(96,96,113,.18)",
                        }}
                      >
                        <DetailMetric
                          label="Costo por unidad de compra"
                          value={formatCost(item.cost)}
                          emphasized
                        />
                        <DetailMetric
                          label="Proveedor"
                          value={item.supplier?.name || "—"}
                        />
                        <DetailMetric
                          label="Unidad de conteo"
                          value={formatUnit(item.stockCountUnit)}
                        />
                        <DetailMetric
                          label="Equivalencia de conteo"
                          value={`1 ${formatUnit(
                            item.stockCountUnit
                          )} = ${formatDecimal(
                            item.consumptionQuantityPerStockUnit
                          )} ${formatUnit(item.consumptionUnit)}`}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}

            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "neutral.700", fontWeight: 600 }}>
                  {loading ? "Cargando ítems de compra..." : emptyMessage}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
